<template>
  <div class="root" v-if="size == 'small'">
    <div class="last5hours">
      <div class="hourly">
        <div class="hour" v-for="(item, index) in last5hours" :key="index">
          <div class="empty" v-if="!item" />
          <template v-else>
            <div class="active" :style="{ height: item.activePercent * 100 + '%' }"></div>
            <div class="inactive" :style="{ height: (1 - item.activePercent) * 100 + '%' }"></div>
          </template>
        </div>
      </div>
      <div
        class="current"
        :class="{
          active: currentlyActive && !disabled,
          inactive: !currentlyActive && !disabled,
          disabled: disabled
        }"
        :title="$t('ALERT_LAST_INACTIVE', { time: lastInactive })"
      ></div>
    </div>
  </div>
  <div class="root" v-else-if="size == 'large'">
    <div class="last5days">
      <div class="day" v-for="d in last5days" :key="d.day">
        <div class="timeline">
          <div class="date" :class="{ today: d.today }">{{ d.dayAbsolute }}</div>
          <div class="hours">
            <div class="hour" v-for="(hour, index) in [6, 12, 18]" :key="index">
              {{ hour }}
              <div class="line" />
            </div>
          </div>
        </div>
        <div class="chunks">
          <div
            class="chunk"
            :class="{ active: chunk.active, inactive: !chunk.active }"
            :style="{
              width: chunk.durationPercent * 100 + '%',
              left: chunk.startPercent * 100 + '%'
            }"
            v-for="chunk in d.chunks"
            :key="chunk.start"
            :title="chunk.description"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.root {
  --active-color: 255, 0, 0;
  --inactive-color: 33, 186, 69;
  --disabled-color: 211, 211, 211;

  .last5days,
  .last5hours {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .last5days {
    gap: 1rem;
  }

  .last5hours {
    gap: 0.3rem;
  }

  .day {
    width: 100%;
  }

  .timeline {
    width: 100%;
    .date {
      &.today {
        font-weight: bold;
      }
      text-align: center;
    }

    .hours {
      display: flex;
      flex-direction: row;
      align-items: center;
      .hour {
        font-size: 0.7rem;
        margin-left: 20%;
      }
      .line {
        width: 1px;
        height: 5px;
        background-color: lightgray;
      }
    }
  }

  .chunks {
    position: relative;
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 20px;

    background-color: lightgray;
    border-radius: 5px;
    overflow: hidden;

    .chunk {
      position: absolute;
      min-height: 100%;
      &.active {
        background-color: rgba(var(--active-color), 1);
      }
      &.inactive {
        background-color: rgba(var(--inactive-color), 1);
      }
    }
  }

  .hourly {
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    height: 1rem;
  }

  .hour {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    margin: 1px;
    border-radius: 2px;
    overflow: hidden;
    div {
      width: 5px;
    }
  }

  .current {
    display: inline-block;
    height: 1em;
    width: 10px;
    border-radius: 2px;
  }

  .current.active {
    box-shadow: 0 0 0 0 rgba(var(--active-color), 1);
    transform: scale(1);
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(var(--active-color), 0.7);
    }

    70% {
      transform: scale(1);
      box-shadow: 0 0 0 10px rgba(var(--active-color), 0);
    }

    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(var(--active-color), 0);
    }
  }

  .empty {
    background-color: lightgray;
    height: 100%;
  }

  .active {
    background-color: rgba(var(--active-color), 1);
  }
  .inactive {
    background-color: rgba(var(--inactive-color), 1);
  }
  .disabled {
    background-color: rgba(var(--disabled-color), 1);
  }
}
</style>

<script>
import { EsteroAlertLog } from 'services'
import _ from 'lodash'
import moment from 'moment'

export default {
  name: 'AlertLog',
  props: {
    alertId: {
      type: String,
      required: true
    },
    size: {
      type: String,
      default: 'small' // small will show last 5 hours, large will show last 24 hours + last 6 days
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    alertLog() {
      return _.find(this.$store.state.estero_alert_log.items, al => al.id === this.alertId)
    },

    currentlyActive() {
      return this.alertLog?.log[this.alertLog.log.length - 1]?.active
    },

    lastInactive() {
      // return the first time the alert was inactive search from the end of the log
      const lastInactive = _.findLastIndex(this.alertLog?.log, { active: false })
      if (lastInactive === -1) {
        return this.$t('NOW')
      }
      return new Date(this.alertLog?.log[lastInactive]?.time * 1000).toLocaleString()
    },

    currentLang() {
      return this.$locale.current()
    },

    last5days() {
      var days = [4, 3, 2, 1, 0]
      return days.map(day => {
        const dayStart = moment()
          .subtract(day, 'days')
          .startOf('day')
          .valueOf()
        const dayEnd = moment()
          .subtract(day, 'days')
          .endOf('day')
          .valueOf()
        const log = this.alertLog?.log.filter(log => {
          const logDate = log.time * 1000
          return logDate >= dayStart && logDate < dayEnd
        })
        // Convert the log into "chunks" of inactive/active time
        let chunks = _.reduce(
          log,
          (chunks, log, index) => {
            // Initial item
            if (index === 0) {
              chunks.push({
                active: log.active,
                logs: [log]
              })
              return chunks
            }

            // if the current logs active property matches the previous logs active property then we are still in the same chunk
            if (log.active === chunks[chunks.length - 1].logs[0].active) {
              chunks[chunks.length - 1].logs.push(log)
            } else {
              chunks.push({
                active: log.active,
                logs: [log]
              })
            }
            return chunks
          },
          []
        )

        // Now we map each chunk to its extremes, min and max time
        chunks = _.map(chunks, chunk => {
          const start = _.minBy(chunk.logs, 'time').time * 1000
          const end = _.maxBy(chunk.logs, 'time').time * 1000

          const fromToDesc = `${moment(start)
            .locale(this.currentLang)
            .format('DD/MM HH:mm')} - ${moment(end)
            .locale(this.currentLang)
            .format('DD/MM HH:mm')}`

          return {
            active: chunk.active,
            start,
            end,
            durationPercent: (end - start) / (dayEnd - dayStart),
            startPercent: (start - dayStart) / (dayEnd - dayStart),
            endPercent: (end - dayStart) / (dayEnd - dayStart),
            description: `${fromToDesc}\n\n${_.join(_.uniq(_.map(chunk.logs, 'message')), '\n')}`
          }
        })

        return {
          dayAbsolute: moment(dayStart)
            .locale(this.currentLang)
            .format('DD/MM'),
          day: -day,
          today: day === 0,
          log,
          chunks,
          activePercent: log?.filter(log => log.active).length / log?.length
        }
      })
    },

    last5hours() {
      return _.reverse(
        _.reduce(
          _.map(
            _.groupBy(
              _.filter(
                this.alertLog?.log,
                l => Date.now() / 1000 - l.time < 4 * 60 * 60 // 5 hours
              ),
              l => Math.round((Date.now() / 1000 - l.time) / (60 * 60))
            ),
            (entries, hoursFromNow) => {
              const activeCount = _.filter(entries, e => e.active).length
              const from = _.orderBy(entries, 'time', 'desc')[0]?.time
              const to = _.orderBy(entries, 'time', 'asc')[0]?.time
              return {
                hoursFromNow,
                from,
                to,
                active: _.some(entries, e => e.active),
                activePercent: activeCount / entries.length,
                activeCount,
                inactiveCount: entries.length - activeCount
              }
            }
          ),
          (chunks, chunk) => {
            chunks[chunk.hoursFromNow] = chunk
            return chunks
          },
          [null, null, null, null, null]
        )
      )
    }
  },

  created() {
    EsteroAlertLog.readAll()
  }
}
</script>
